import { useEffect, useState } from 'react'
import { onIntegrationsReady } from './integrations-bootstrap'

function readAiEnabled(): boolean {
  const ai = (window.__TENANT_CONFIG__ as { ai?: { enabled?: boolean } } | undefined)?.ai
  return ai?.enabled === true
}

interface AiEnabledState {
  /** True only when the integrations bootstrap confirmed ai.enabled === true. */
  enabled: boolean
  /** False until the integrations bootstrap fires; route guards must wait on this. */
  ready: boolean
}

/**
 * Reactive flag for the per-tenant AI integration.
 *
 * Strict: `enabled` is true only when the integrations bootstrap reports
 * `ai.enabled === true`. A missing block or `enabled: false` both return
 * false. Use this to gate every AI surface (sidebar item, floating bubble,
 * /ai route) so unconfigured tenants don't expose a button that 401s.
 *
 * `ready` is false until the integrations bootstrap completes. Surfaces that
 * can safely flicker (sidebar item, floating bubble) ignore `ready` — they
 * just hide-then-show if AI is actually enabled. The route guard MUST wait
 * on `ready` to avoid redirecting away from /ai during the brief loading
 * window on tenants who DO have AI enabled.
 *
 * The bootstrap fires its listeners exactly once on success OR failure, so
 * `ready` always settles. No polling, no timeout.
 */
export function useAiEnabled(): AiEnabledState {
  const [state, setState] = useState<AiEnabledState>(() => {
    // If __TENANT_CONFIG__.ai is already populated when this hook first
    // renders (i.e. the middleware-injected config or a finished bootstrap),
    // we can settle synchronously. Detect "already populated" by checking
    // for the ai key's presence — undefined means bootstrap hasn't run.
    const hasAiBlock = (window.__TENANT_CONFIG__ as { ai?: unknown } | undefined)?.ai !== undefined
    return { enabled: readAiEnabled(), ready: hasAiBlock }
  })

  useEffect(() => {
    if (state.ready) return
    onIntegrationsReady(() => {
      setState({ enabled: readAiEnabled(), ready: true })
    })
  }, [state.ready])

  return state
}
